import * as d3 from 'd3';
import React from 'react';
import styled from 'styled-components';

import {useTranslation} from "react-i18next";
import Colors from '../../../../assets/colors/Colors';

interface Country {
    name: string,
    gip: number,
    alpha2: string,
    alpha3: string
}

interface Props {
    countries: Country[],
    maxGip: number,
    firstPageLength: number,
    isLife?: boolean,
    width?: number,
}

interface VerticalAxisProps {
    scale: d3.ScaleLinear<number, number>
}

interface BarProps {
    xScale: d3.ScaleLinear<number, number>,
    yScale: d3.ScaleLinear<number, number>,
    index: number,
    country: Country,
    countriesLength: number,
    firstPageLength: number
}

const MARGIN = {
    top: 10,
    bottom: 40,
    left: 40,
    right: 24
};

const DIMENSIONS = {
  width: 320,
  height: 210
};

const MIN_BAR_HEIGHT = 15;
const VERTICAL_SPINE_WIDTH = 7;
const BAR_WIDTH = 24;

const StyledBar = styled.rect``;

const StyledAxisLabel = styled.text`
  font-size: 0.5625rem;
  font-family: Mapfre-Light;
  text-anchor: middle;
  stroke: none;
`;

const StyledBarchart = styled.svg`
  overflow: hidden;

  & .barchart__horizontal-axis {
    stroke-width: 3px;
    stroke-linecap: butt;
    fill: none;
  }

  & .vertical-axis__spine {
    stroke-width: 3px;
    stroke-linecap: butt;
  }
`;

const Bar = (props: BarProps) => {
  const { t } = useTranslation();

  const c = props.country;
  const scaledGip = props.yScale(c.gip);

  const barWidth = BAR_WIDTH;
  const barX = props.xScale(props.index + 0.5);
  const barMidX = barX + barWidth / 2;
  const barHeight = scaledGip > MIN_BAR_HEIGHT ? scaledGip : MIN_BAR_HEIGHT;
  const barY = DIMENSIONS.height - MARGIN.bottom - barHeight;
  const barBorderRadius = barHeight > 30 ? 10 : 7;

  return (
    <g key={`${c.name}-bar`}>
      <StyledBar
        width={barWidth}
        height={barHeight}
        x={barX}
        y={barY}
        rx={barBorderRadius}
        ry={barBorderRadius}
      />
      <StyledAxisLabel
        x={`${barMidX}px`}
        y={`${barY + barHeight + 16}px`}
      >
        {t(`countries.names.${c.alpha2}`)}
      </StyledAxisLabel>
    </g>
  )
};

const VerticalAxis = (props: VerticalAxisProps) => {
    const scale = props.scale;
    const ticks = scale.ticks(6);

    return (
        <React.Fragment>
            <path
                style={{
                    strokeWidth: '3px',
                    strokeLinecap: 'butt',
                    fill: 'none'
                }}
                d={`M${MARGIN.left},${MARGIN.top}L${MARGIN.left},${DIMENSIONS.height - MARGIN.bottom}`}
            />
            {ticks.map(tick => (
              <g key={ tick }>
                <StyledAxisLabel
                  x={ MARGIN.left - VERTICAL_SPINE_WIDTH - 2 }
                  y={ DIMENSIONS.height - MARGIN.bottom - scale(tick) + 2 }
                  style={{
                    textAnchor: 'end'
                  }}
                >
                  { tick.toFixed(2) }
                </StyledAxisLabel>
                <line
                  x1={ MARGIN.left }
                  x2={ MARGIN.left - VERTICAL_SPINE_WIDTH}
                  y1={ DIMENSIONS.height - MARGIN.bottom - scale(tick) }
                  y2={ DIMENSIONS.height - MARGIN.bottom - scale(tick) }
                  className='vertical-axis__spine'
                />
              </g>
            ))}
        </React.Fragment>
    )
};

const HorizontalAxis = (props: { width: number }) => {
  return (
    <path
      className='barchart__horizontal-axis'
      d={`M${MARGIN.left},${DIMENSIONS.height - MARGIN.bottom}L${props.width - MARGIN.left},${DIMENSIONS.height - MARGIN.bottom}`}
    />
  );
};

const Bars = (props: Props) => {
    // const countries = props.countries.sort((a: Country, b:Country): number => b.gip - a.gip);
    const countries = props.countries;

    const xScale = d3.scaleLinear()
        .domain([0, countries.length + 1])
        .range([MARGIN.left, props.width || 0]);

    const yScale = d3.scaleLinear()
        // @ts-ignore
        .domain([0, props.maxGip])
        .range([0, DIMENSIONS.height - MARGIN.bottom - MARGIN.top]);

    return (
        <React.Fragment>
            <VerticalAxis scale={yScale}/>
            <HorizontalAxis width={ props.width || 0 }/>
            {countries.map((c: Country, i: number) => (
                <Bar
                  key={i}
                  xScale={xScale}
                  yScale={yScale}
                  index={i}
                  country={c}
                  countriesLength={countries.length}
                  firstPageLength={props.firstPageLength}
                />
              ))
            }
        </React.Fragment>
    )
};

const BarChart = (props: Props) => {
  const width = MARGIN.left + BAR_WIDTH * 2 * props.countries.length + MARGIN.right;

  return (
    <StyledBarchart
      viewBox={ `0 0 ${ width } ${ DIMENSIONS.height }` }
      stroke={props.isLife ? Colors.fontWhite : Colors.fontGray}
      fill={props.isLife ? Colors.fontWhite : Colors.fontGray}
      width={ width }
    >
      <Bars { ...props } width={ width } />
    </StyledBarchart>
  )
};

export default BarChart;
